import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import InputText from "core/inputs/input-text/InputText";
import InputCheckbox from "core/inputs/input-checkbox/InputCheckbox";
import InputRadioGroup from "core/inputs/input-radio/InputRadioGroup";
import { Typography } from "core/typography/Typography";
import { Controller } from "utils/Controller";
import { renderInputCurrency } from "components/form/CommonInputs";
import {
  DELIVERY_NOTE_NUMBER_EXAMPLE,
  DELIVERY_NOTE_PDF_NAME_MODES,
} from "constants/constants";
import {
  DELIVERY_NOTE_FORM_LABELS,
  DELIVERY_NOTE_FORM_PLACEHOLDERS,
  DELIVERY_NOTE_PDF_NAME_LABELS,
} from "constants/forms";
import { buildPdfFileNameExample } from "utils/deliveryNoteHelper";

const PDF_SUFFIX_OPTION = "pdf-suffix";

const PDF_NAME_MODE_OPTIONS = [
  {
    val: DELIVERY_NOTE_PDF_NAME_MODES.AUTOMATIC,
    lb: DELIVERY_NOTE_PDF_NAME_LABELS.automatic,
  },
  {
    val: DELIVERY_NOTE_PDF_NAME_MODES.CUSTOM,
    lb: DELIVERY_NOTE_PDF_NAME_LABELS.custom,
  },
];

const AUTOMATIC_FILE_NAME_EXAMPLE = `${DELIVERY_NOTE_NUMBER_EXAMPLE}.pdf`;

const REQUIRED_TEXT_RULES = {
  required: true,
  validate: (value) => !!value?.trim(),
};

const IdentityBlock = ({
  control,
  register,
  errors,
  watch,
  setValue,
  currencyList,
}) => {
  const intl = useIntl();
  const pdfSuffixEnabled = watch("pdf_suffix_enabled");
  const pdfFileNameMode = watch("pdf_file_name_mode");
  const isCustomPdfName =
    pdfFileNameMode === DELIVERY_NOTE_PDF_NAME_MODES.CUSTOM;

  const renderTextCell = ({ name, required, label }) => (
    <Controller
      control={control}
      register={register}
      name={name}
      rules={required ? REQUIRED_TEXT_RULES : undefined}
      render={(props) => (
        <InputText
          id={`delivery-note-${name}`}
          errors={errors[name]}
          label={label || DELIVERY_NOTE_FORM_LABELS[name]}
          placeholder={DELIVERY_NOTE_FORM_PLACEHOLDERS[name]}
          required={required}
          {...props}
        />
      )}
    />
  );

  const renderCustomPdfName = () => {
    const example =
      buildPdfFileNameExample({
        fileName: watch("pdf_file_name"),
        withSuffix: pdfSuffixEnabled,
      }) || AUTOMATIC_FILE_NAME_EXAMPLE;

    return (
      <>
        {renderTextCell({
          name: "pdf_file_name",
          label: DELIVERY_NOTE_PDF_NAME_LABELS.field,
        })}
        <InputCheckbox
          id="delivery-note-pdf-suffix"
          options={[
            {
              val: PDF_SUFFIX_OPTION,
              lb: intl.formatMessage(DELIVERY_NOTE_PDF_NAME_LABELS.suffix),
            },
          ]}
          value={pdfSuffixEnabled ? [PDF_SUFFIX_OPTION] : []}
          getSelectedValue={(values) =>
            setValue("pdf_suffix_enabled", values.includes(PDF_SUFFIX_OPTION))
          }
        />
        <Typography
          className="text-3 violet"
          type="p"
          label={
            <FormattedMessage
              {...DELIVERY_NOTE_PDF_NAME_LABELS.example}
              values={{ example: <strong>{example}</strong> }}
            />
          }
        />
      </>
    );
  };

  const renderAutomaticPdfName = () => (
    <Typography
      className="text-3 violet"
      type="p"
      label={
        <FormattedMessage
          {...DELIVERY_NOTE_PDF_NAME_LABELS.automaticHint}
          values={{ example: <strong>{AUTOMATIC_FILE_NAME_EXAMPLE}</strong> }}
        />
      }
    />
  );

  return (
    <div className="delivery-note-form__section">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage
            id="delivery_note_identity_block"
            defaultMessage="Identité"
          />
        }
      />
      <div className="delivery-note-grid">
        <div className="line-1">
          <div className="cell-padding">
            {renderTextCell({ name: "name", required: true })}
          </div>
        </div>
        <div className="line-2">
          <div className="cell-padding">
            {renderTextCell({ name: "title", required: true })}
          </div>
          <div className="cell-padding">
            {renderTextCell({ name: "subtitle" })}
          </div>
          <div className="cell-padding delivery-note-grid__currency">
            {renderInputCurrency({
              control,
              register,
              setValue,
              currencyList,
              label: DELIVERY_NOTE_FORM_LABELS.currency,
              value: watch("currency_id"),
              errors,
              required: false,
            })}
          </div>
        </div>
        <div className="line-3">
          <div className="cell-padding delivery-note-grid__pdf-name">
            <InputRadioGroup
              id="delivery-note-pdf-name-mode"
              label={DELIVERY_NOTE_FORM_LABELS.pdf_file_name}
              options={PDF_NAME_MODE_OPTIONS.map((option) => ({
                val: option.val,
                lb: intl.formatMessage(option.lb),
              }))}
              value={pdfFileNameMode}
              getSelectedValue={(event) =>
                setValue("pdf_file_name_mode", event.target.value)
              }
            />
            {isCustomPdfName ? renderCustomPdfName() : renderAutomaticPdfName()}
          </div>
        </div>
      </div>
    </div>
  );
};

IdentityBlock.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  currencyList: PropTypes.array,
};

export default IdentityBlock;
